<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">Insurers</h1>
      </template>
      <template #actions>
        <page-header-action
          tag="form-button"
          @clicked="openModal"
          v-if="hasPermission('insurers.create')"
        >
          New Insurer
        </page-header-action>
      </template>
    </page-header>
    <div class="row">
      <div class="col-lg-12">
        <div class="card mb-3">
          <div class="card-header">
            <div style="width: 100%; margin: 0;">
              <label for="handler_id" style="margin-top: 0;">Terms</label>

              <input
                type="text"
                class="form-control"
                placeholder="Search..."
                :value="filters.terms"
                @input="setTerms($event.target.value)"
              />
            </div>
          </div>

          <div class="loading-overlay" v-if="loading"></div>

          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Band</th>
                  <th>Email Address</th>
                  <th>Telephone Number</th>
                  <th>GTA</th>
                  <th>Has Protocol</th>
                  <th></th>
                </tr>
              </thead>
              <tbody v-if="insurers.length">
                <tr v-bind:key="index" v-for="(insurer, index) in insurers">
                  <td>{{ insurer.name }}</td>
                  <td>{{ insurer.band }}</td>
                  <td>{{ insurer.email }}</td>
                  <td>{{ insurer.telephone }}</td>
                  <td>
                    <span class="badge badge-success" v-if="insurer.gta"
                      >Yes</span
                    >
                    <span class="badge badge-danger" v-if="!insurer.gta"
                      >No</span
                    >
                  </td>
                  <td>
                    <span
                      class="badge badge-success"
                      v-if="insurer.protocol_tariff"
                      >Yes</span
                    >
                    <span
                      class="badge badge-danger"
                      v-if="!insurer.protocol_tariff"
                      >No</span
                    >
                  </td>
                  <td class="text-right">
                    <button
                      class="btn"
                      :class="{
                        'btn-warning': hasPermission('insurers.update'),
                        'btn-info': !hasPermission('insurers.update')
                      }"
                      v-on:click.prevent="openEditModal(insurer)"
                    >
                      {{ hasPermission('insurers.update') ? 'Edit' : 'View' }}
                    </button>
                    <button
                      class="btn btn-danger"
                      v-on:click.prevent="removeInsurer(insurer)"
                      v-if="hasPermission('insurers.delete')"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              </tbody>
              <tbody v-if="!insurers.length">
                <tr>
                  <td colspan="6" class="text-center">
                    There are currently no insurers
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="card-footer text-right" v-show="showPagination">
            <pagination
              :data="paginator_data"
              v-on:updateResults="getData"
              :params="`&per_page=${perPage}&parts=protocolTariff`"
            ></pagination>
          </div>
        </div>
      </div>
    </div>

    <modal :show="modals.form" :large="true" v-on:close="closeModal">
      <div slot="title">
        <h4 v-text="formModalTitle"></h4>
      </div>
      <div class="row">
        <div class="col-xl-12">
          <div class="form-horizontal">
            <inputbox
              name="name"
              label="Name"
              :required="true"
              v-model="form.name"
              :show-error="form.errors.has('name')"
              :error-message="form.errors.get('name')"
              :readonly="!hasPermission('insurers.update')"
            ></inputbox>

            <selectlist
              :options="bands"
              v-model="form.band"
              label="Band"
              name="band"
              placeholder="Please Select"
              :show-error="form.errors.has('band')"
              :error-message="form.errors.get('band')"
              :style="'align-items:flex-start'"
            ></selectlist>

            <inputbox
              name="provision"
              label="Provision"
              v-model="form.provision"
              :show-error="form.errors.has('provision')"
              :error-message="form.errors.get('provision')"
              :readonly="!hasPermission('insurers.update')"
            ></inputbox>

            <inputbox
              name="email"
              label="Email Address"
              type="email"
              v-model="form.email"
              :show-error="form.errors.has('email')"
              :error-message="form.errors.get('email')"
              :readonly="!hasPermission('insurers.update')"
            ></inputbox>

            <inputbox
              name="telephone"
              label="Telephone Number"
              type="phone"
              v-model="form.telephone"
              :show-error="form.errors.has('telephone')"
              :error-message="form.errors.get('telephone')"
              :readonly="!hasPermission('insurers.update')"
            ></inputbox>

            <div class="form-group">
              <div class="col-lg-4">
                <label class="control-label">GTA</label>
              </div>
              <div class="col-lg-8">
                <toggleswitch
                  :labels="true"
                  name="gta"
                  v-model="form.gta"
                  v-if="hasPermission('insurers.update')"
                ></toggleswitch>

                <span
                  v-else
                  class="badge"
                  :class="{
                    'badge-success': form.gta,
                    'badge-danger': !form.gta
                  }"
                  >{{ form.gta ? 'Yes' : 'No' }}</span
                >
              </div>
            </div>

            <div class="form-group">
              <div class="col-lg-4">
                <label class="control-label">Has Protocol Rates?</label>
              </div>
              <div class="col-lg-8">
                <toggleswitch
                  :labels="true"
                  name="enableProtocolRates"
                  v-model="form.protocol_tariff"
                  @change="toggleRate"
                  v-if="hasPermission('insurers.update')"
                ></toggleswitch>

                <span
                  v-else
                  class="badge"
                  :class="{
                    'badge-success': form.protocol_tariff,
                    'badge-danger': !form.protocol_tariff
                  }"
                  >{{ form.protocol_tariff ? 'Yes' : 'No' }}</span
                >
              </div>
            </div>

            <!-- Protocol Tariff Table -->
            <fieldset v-if="form.protocol_tariff">
              <legend>Protocol Tariff</legend>
              <protocol-tariff
                :rates-form="ratesForm"
                :readonly="!hasPermission('insurers.update')"
              ></protocol-tariff>
            </fieldset>
          </div>
        </div>
      </div>
      <div slot="footer" v-if="hasPermission('insurers.update')">
        <button
          class="btn btn-danger float-left"
          v-on:click.prevent="closeModal"
        >
          Close
        </button>
        <form-button :indicator="saving" v-on:clicked="saveForm">
          Save Insurer
        </form-button>
      </div>
    </modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import ProtocolTariff from '@/components/Modules/Shared/Tariff.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  components: {
    ProtocolTariff,
    PageHeader,
    PageHeaderAction
  },
  data() {
    return {
      insurers: [],
      paginator_data: {},
      modals: {
        form: false
      },
      loading: false,
      saving: false,
      editingInsurer: null,
      filters: {
        terms: ''
      },
      bands: [
        { id: 'A', label: 'A' },
        { id: 'B', label: 'B' },
        { id: 'C', label: 'C' },
        { id: 'D', label: 'D' }
      ],
      form: new window.Form({
        name: null,
        band: null,
        provision: null,
        email: null,
        telephone: null,
        gta: false,
        protocol_tariff: false
      }),
      ratesForm: new window.Form({
        rates: [],
        storage_daily_rate: null,
        storage_cap: null
      }),
      gtaRate: [],
      nonGtaRate: [],
      protocolTariffEdit: false,
      protocolTariffId: null
    };
  },

  computed: {
    formModalTitle() {
      if (this.editingInsurer !== null) {
        return 'Edit Insurer';
      }

      return 'Add Insurer';
    },

    showPagination() {
      return (
        this.paginator_data.next_page_url || this.paginator_data.prev_page_url
      );
    }
  },

  mounted() {
    this.$store.commit('CreditHireCharges/retrieve');
    this.$store.commit('CreditHireChargeTypes/retrieve');
    this.$store.commit('VehicleGroups/retrieve');
    this.getAllInsurers();
    this.fetchRates();
  },

  methods: {
    getAllInsurers() {
      this.getData(`/insurers?parts=protocolTariff&per_page=${this.perPage}`);
    },

    getData(url) {
      Object.keys(this.filters).forEach(key => {
        url = `${url}&filter[${key}]=${this.filters[key]}`;
      });

      this.loading = true;

      window.Ajax.get(url)
        .then(response => {
          this.insurers = response.data.data;
          this.paginator_data = response.data;
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
        });
    },

    saveForm() {
      let method = 'post';
      let url = `/insurers`;

      if (this.editingInsurer !== null) {
        method = 'put';
        url = `/insurers/${this.editingInsurer.identifier}`;
      }

      this.saving = true;

      let ptEnabled = this.form.protocol_tariff;

      this.form[method](url)
        .then(response => {
          if (this.editingInsurer !== null) {
            const index = this.insurers.indexOf(this.editingInsurer);

            this.insurers[index] = response;

            if (!ptEnabled && this.protocolTariffId) {
              this.removeProtocolTariff();
            }

            this.saving = false;

            this.closeModal();
          } else {
            this.saving = false;
            this.insurers.push(response);
            this.closeModal();
          }

          if (ptEnabled) {
            this.processTariffs(response);
          }

          window.toastify.success(
            'Saved',
            'The insurer was saved successfully!'
          );
        })
        .catch(error => {
          this.saving = false;
          window.toastify.error(
            'Error',
            'Sorry, there was an issue saving the insurer'
          );
        });
    },

    removeProtocolTariff() {
      window.Ajax.delete(`/hires/tariffs/${this.protocolTariffId}`)
        .then(response => {
          this.protocolTariffId = null;
          this.protocolTariffEdit = false;
          this.ratesForm.rates = [];
          this.getAllInsurers();
          this.fetchRates();
        })
        .catch(err => {
          window.toastify.error(
            'Error',
            'Sorry, there was an issue deleting the tariff.'
          );
        });
    },

    processTariffs(insurer) {
      let method = 'post';
      let url = `/hires/tariffs`;

      if (this.protocolTariffId) {
        method = 'put';
        url = `/hires/tariffs/${this.protocolTariffId}`;
      }

      const payload = {
        insurer_id: insurer.identifier,
        rates: this.ratesForm.rates,
        storage_daily_rate: this.ratesForm.storage_daily_rate,
        storage_cap: this.ratesForm.storage_cap
      };

      window.Ajax[method](url, payload)
        .then(response => {
          // At this point, the ProtocolTariff attachment is still empty.
          // Reload insurers to ensure they reflect the newly created ProtocolTariff.
          this.getAllInsurers();
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            'Sorry, there was an error saving the tariff data'
          );
        });
    },

    removeInsurer(insurer) {
      if (this.hasPermission('insurers.delete')) {
        window
          .swal({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete the insurer!'
          })
          .then(() => {
            window.Ajax.delete(`/insurers/${insurer.identifier}`)
              .then(response => {
                window.toastify.success('Success', 'The insurer was deleted.');

                const key = this.insurers.indexOf(insurer);

                this.insurers.splice(key, 1);
              })
              .catch(err => {
                window.toastify.error(
                  'Error',
                  'Sorry, there was an issue deleting the insurer.'
                );
              });
          });
      }
    },

    openModal() {
      this.modals.form = true;
      this.toggleRate();
    },

    closeModal() {
      this.modals.form = false;
      this.editingInsurer = null;
      this.form.reset();
    },

    openEditModal(insurer) {
      this.editingInsurer = insurer;

      this.form.name = insurer.name;
      this.form.band = insurer.band;
      this.form.provision = insurer.provision;
      this.form.email = insurer.email;
      this.form.telephone = insurer.telephone;
      this.form.gta = insurer.gta;
      this.modals.form = true;
      this.form.protocol_tariff = insurer.protocol_tariff || false;

      this.protocolTariffId = insurer?.protocolTariff?.identifier || null;
      this.protocolTariffEdit = !!insurer?.protocolTariff?.rates?.length;

      const tariffs = insurer?.protocolTariff?.rates || [];

      if (tariffs.length > 0) {
        // Load existing rates from database
        this.ratesForm.rates = tariffs.map(tariff => ({
          charge_id: tariff.charge_id,
          group_id: tariff.abi_group_id,
          charge_type_id: tariff.charge_type_id,
          rate: tariff.rate
        }));
        this.ratesForm.storage_daily_rate =
          insurer?.protocolTariff?.storage_daily_rate || null;
        this.ratesForm.storage_cap =
          insurer?.protocolTariff?.storage_cap || null;
      } else if (this.form.protocol_tariff) {
        // Pre-populate with GTA or non-GTA rates if no database rates
        this.ratesForm.rates = this.form.gta
          ? [...this.gtaRate]
          : [...this.nonGtaRate];
        this.ratesForm.storage_daily_rate = null;
        this.ratesForm.storage_cap = null;
      } else {
        this.ratesForm.reset();
      }
    },

    toggleRate() {
      if (this.form.protocol_tariff) {
        this.ratesForm.rates = this.form.gta
          ? [...this.gtaRate]
          : [...this.nonGtaRate];
      } else {
        this.ratesForm.reset();
      }
    },

    fetchRates() {
      this.fetchTariff('gta', 'gtaRate');
      this.fetchTariff('non_gta', 'nonGtaRate');
    },

    fetchTariff(type, stateKey) {
      window.Ajax.get(`/hires/tariffs?filter[type]=${type}`)
        .then(response => {
          const tariffs = response.data;

          this[stateKey] = tariffs
            .filter(tariff => tariff.name === type)
            .flatMap(tariff =>
              tariff.rates.map(rate => ({
                charge_id: rate.charge_id,
                group_id: rate.abi_group_id || null,
                charge_type_id: rate.charge_type_id,
                rate: rate.rate
              }))
            );
        })
        .catch(error => {
          console.error(error);
          window.toastify.error('Error', `Failed to fetch ${type} tariffs.`);
        });
    },

    setTerms: debounce(function(terms) {
      this.filters.terms = terms;

      this.getAllInsurers();
    }, 400)
  }
};
</script>
